import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, CheckCircle2, XCircle, Search, Save, X, Megaphone } from 'lucide-react';

const Campaigns = () => {
    const { authFetch } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', active: true });

    // Create new form
    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', active: true });

    useEffect(() => {
        fetchCampaigns();
        // eslint-disable-next-line
    }, []);

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/backoffice/campaigns');
            if (res.ok) {
                const data = await res.json();
                setCampaigns(data);
            }
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!createForm.name.trim()) return;
        try {
            const res = await authFetch('/api/backoffice/campaigns', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(createForm)
            });
            if (res.ok) {
                const newCamp = await res.json();
                setCampaigns([newCamp, ...campaigns]);
                setIsCreating(false);
                setCreateForm({ name: '', active: true });
            }
        } catch (error) {
            console.error("Error creating campaign:", error);
        }
    };

    const handleUpdate = async (id) => {
        if (!editForm.name.trim()) return;
        try {
            const res = await authFetch(`/api/backoffice/campaigns/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm)
            });
            if (res.ok) {
                const updatedCamp = await res.json();
                setCampaigns(campaigns.map(c => c.id === id ? updatedCamp : c));
                setEditingId(null);
            }
        } catch (error) {
            console.error("Error updating campaign:", error);
        }
    };

    const startEdit = (campaign) => {
        setEditingId(campaign.id);
        setEditForm({ name: campaign.name, active: campaign.active });
    };

    const filteredCampaigns = campaigns.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold">Campañas</h1>
                    <p className="text-slate-400 text-sm">Gestiona las campañas disponibles para los agentes.</p>
                </div>
                {!isCreating && (
                    <button
                        onClick={() => setIsCreating(true)}
                        className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                    >
                        <Plus size={18} />
                        <span>Nueva Campaña</span>
                    </button>
                )}
            </div>

            {/* Quick Create Form */}
            {isCreating && (
                <div className="bg-slate-800/50 border border-primary/30 p-5 rounded-2xl flex items-center gap-4 animate-in slide-in-from-top-4 duration-300">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Nombre de la nueva campaña..."
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-slate-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            value={createForm.name}
                            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                            autoFocus
                        />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300 bg-slate-900/50 px-4 py-2.5 rounded-xl border border-slate-700">
                        <input
                            type="checkbox"
                            className="accent-primary w-4 h-4 cursor-pointer rounded"
                            checked={createForm.active}
                            onChange={(e) => setCreateForm({ ...createForm, active: e.target.checked })}
                        />
                        <span>Activa</span>
                    </label>
                    <button
                        onClick={handleCreate}
                        disabled={!createForm.name.trim()}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        <Save size={18} />
                        Guardar
                    </button>
                    <button
                        onClick={() => setIsCreating(false)}
                        className="p-2.5 text-slate-400 hover:text-slate-200 bg-slate-800/50 hover:bg-slate-700/50 rounded-xl transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            )}

            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                {/* Search Bar */}
                <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                    <div className="relative max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-slate-500" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar campañas..."
                            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-slate-800 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/80">
                    <div className="col-span-6">Campaña</div>
                    <div className="col-span-3 text-center">Estado</div>
                    <div className="col-span-3 text-right pr-4">Acciones</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-slate-800/50">
                    {loading ? (
                        <div className="p-8 text-center text-slate-500 animate-pulse">Cargando campañas...</div>
                    ) : filteredCampaigns.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No se encontraron campañas.</div>
                    ) : (
                        filteredCampaigns.map(camp => (
                            <div key={camp.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-800/30 transition-colors group">
                                {/* Name Column */}
                                <div className="col-span-6 flex items-center gap-3 font-medium text-slate-200">
                                    <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-500 border border-slate-700 group-hover:border-slate-600 transition-colors">
                                        <Megaphone size={14} />
                                    </div>
                                    {editingId === camp.id ? (
                                        <input
                                            type="text"
                                            className="bg-slate-950 border border-primary/50 rounded flex-1 px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            autoFocus
                                        />
                                    ) : (
                                        <span className="truncate pr-4">{camp.name}</span>
                                    )}
                                </div>

                                {/* Status Column */}
                                <div className="col-span-3 flex justify-center">
                                    {editingId === camp.id ? (
                                        <select
                                            className="bg-slate-950 border border-primary/50 rounded px-2 py-1.5 text-sm focus:outline-none"
                                            value={editForm.active ? "true" : "false"}
                                            onChange={(e) => setEditForm({ ...editForm, active: e.target.value === "true" })}
                                        >
                                            <option value="true">Activa</option>
                                            <option value="false">Inactiva</option>
                                        </select>
                                    ) : (
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${camp.active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                                            {camp.active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                                            {camp.active ? 'Activa' : 'Inactiva'}
                                        </span>
                                    )}
                                </div>

                                {/* Actions Column */}
                                <div className="col-span-3 flex justify-end gap-2">
                                    {editingId === camp.id ? (
                                        <>
                                            <button
                                                onClick={() => handleUpdate(camp.id)}
                                                className="p-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded border border-emerald-500/30 transition-colors"
                                                title="Guardar"
                                            >
                                                <Save size={16} />
                                            </button>
                                            <button
                                                onClick={() => setEditingId(null)}
                                                className="p-1.5 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded border border-slate-600 transition-colors"
                                                title="Cancelar"
                                            >
                                                <X size={16} />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => startEdit(camp)}
                                            className="p-1.5 text-slate-500 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                            title="Editar"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Campaigns;
